import type {
  AppleAuthPayload,
  AuthUserDto,
  AuthValidateOtpPayload,
  JWT,
  OtpMailData,
  RegisterFormData,
  UserFormData,
} from '@/core/data/auth/auth.interface';
import type { LoginMethodType } from '@/core/data/auth/auth.type';
import type { CodeResponse } from '@/core/data/auth/google/oauth2';
import type { JwtRepository } from '@/core/data/auth/jwt.repository';
import { StorageKeyType, type StorageRepositoryInterface } from '@/core/data/storage/storage.interface';
import type { UserRepository } from '@/core/data/user/user.repository';
import { readHandoffCookie, writeHandoffCookie } from '@/core/helpers/handoff-cookie.helper';
import { createLogger, formatUnknownError } from '@/core/helpers/logger.helper';
import { isIOS, isIPadOS, isPWA } from '@/core/helpers/ui.helper';
import type { AuthApi } from '@/core/network/api/auth.api';
import { HandleApiError } from '@/core/network/http/httpError';
import type { UserRatings } from '../rating/rating.interface';
import type { BaseUserInfo } from '../user/user.interface';

const logger = createLogger('AuthRepository');

export type EnhancedAuthUserDto = AuthUserDto & { info: BaseUserInfo | undefined };

export class AuthRepository {
  private readonly authApi: AuthApi;
  private readonly jwtRepository: JwtRepository;
  private readonly userRepository: UserRepository;
  private readonly storage: StorageRepositoryInterface;
  private readonly session: StorageRepositoryInterface;

  constructor(
    jwtRepository: JwtRepository,
    userRepository: UserRepository,
    AuthApi: AuthApi,
    storage: StorageRepositoryInterface,
    session: StorageRepositoryInterface,
  ) {
    this.authApi = AuthApi;
    this.jwtRepository = jwtRepository;
    this.userRepository = userRepository;
    this.storage = storage;
    this.session = session;
  }

  async authWithEmailUsername(value: string): Promise<boolean> {
    try {
      const response = await this.authApi.authWithEmailUsername(value);
      if (response?.status === 201) {
        this.session.set<OtpMailData>(StorageKeyType.OtpSend, {
          email: response?.data.data?.email || '',
          sendTime: Date.now(),
        });
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  private async setAuthData(token: JWT, statusCode: number): Promise<EnhancedAuthUserDto | null> {
    this.jwtRepository.setJWToken(token);
    // Every login path funnels through here, and the cookie has to be minted on
    // the visit the user actually signs in on - bootstrap alone misses it,
    // because no token exists yet at that point. Fire and forget: sign-in must
    // not wait on it.
    void this.ensureHandoffCookie();
    if (statusCode >= 200 && statusCode < 300) {
      this.storage.remove(StorageKeyType.OtpSend);
      const userInfo = await this.userRepository.getUserInfo(true);
      // @ts-ignore
      return { ...this.jwtRepository.getUser(), info: userInfo?.data };
    }
    return null;
  }

  async authValidateOtp(payload: AuthValidateOtpPayload): Promise<EnhancedAuthUserDto | null> {
    try {
      const response = await this.authApi.authValidateOtp(payload);
      return response?.data.data ? await this.setAuthData(response.data.data, response.status) : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async authGoogle(code: CodeResponse): Promise<AuthUserDto | null> {
    try {
      const response = await this.authApi.authWithGoogle(code);
      return response?.data.data ? await this.setAuthData(response.data.data, response.status) : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async authApple(payload: AppleAuthPayload): Promise<AuthUserDto | null> {
    try {
      const response = await this.authApi.authWithApple(payload);
      return response?.data.data ? await this.setAuthData(response.data.data, response.status) : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getToken(code: string): Promise<AuthUserDto | null> {
    try {
      const response = await this.authApi.authGetToken(code);
      return response?.data.data ? await this.setAuthData(response.data.data, response.status) : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  /**
   * Parks a single-use code in a cookie for a future home screen install to
   * spend. Never throws: a missing cookie only costs the install a sign-in.
   */
  async ensureHandoffCookie(): Promise<void> {
    // An installed app has nothing to hand off to, and its own cookie is the
    // one being redeemed.
    if (isPWA.value) return;

    // Only iOS hands a home screen app its own empty storage. Android and
    // desktop installs run on the browser's profile and are signed in already,
    // so parking a credential there buys nothing. iPadOS is checked separately
    // because it reports itself as macOS while partitioning like an iPhone.
    if (!isIOS.value && !isIPadOS.value) return;

    const userId = this.jwtRepository.getUser()?.id;
    if (!userId) return;

    // Reissued on account change rather than skipped whenever any cookie
    // exists, otherwise the previous account's code stays parked.
    if (readHandoffCookie()?.userId === userId) return;

    try {
      const response = await this.authApi.authCreateHandoff();
      const handoff = response?.data.data;
      if (handoff?.code) {
        writeHandoffCookie({ userId, code: handoff.code }, handoff.expiresIn);
      } else {
        logger.warn('Hand-off code request returned no code; install will require a sign in.');
      }
    } catch (e) {
      logger.warn(`Could not park a hand-off code: ${formatUnknownError(e)}`);
    }
  }

  async revokeHandoffCode(code: string): Promise<void> {
    try {
      await this.authApi.authRevokeHandoff(code);
    } catch (e) {
      // Sign-out must not be blocked by this; the code expires on its own.
      logger.warn(`Could not revoke hand-off code: ${formatUnknownError(e)}`);
    }
  }

  async logout(): Promise<void> {
    try {
      // Ends this session server side, so the refresh token cannot outlive the
      // sign out. Goes first, while there is still a token to authenticate it.
      await this.authApi.logout();
    } catch (e) {
      // Never block the sign out on this. Refusing to sign someone out because
      // the network is down is worse than a session row living until its
      // refresh token expires.
      logger.warn(`Could not end the session server side: ${formatUnknownError(e)}`);
    }

    await this.jwtRepository.logout();
  }

  async submitPersonalInfo(formData: UserFormData): Promise<AuthUserDto | null> {
    try {
      const response = await this.authApi.submitPersonalInfo(formData);

      if (response?.data.data) {
        this.removeOnboardingData();
        return await this.setAuthData(response.data.data, response.status);
      }
      return null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async submitRatings(formData: UserRatings): Promise<AuthUserDto | null> {
    try {
      const response = await this.authApi.submitRatings(formData);

      if (response?.data.data) {
        this.removeOnboardingData();
        return await this.setAuthData(response.data.data, response.status);
      }
      return null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async deleteUser(): Promise<boolean> {
    try {
      const response = await this.authApi.deleteUser();
      return response.status >= 200 && response.status < 300;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  isAuthenticated(): boolean {
    return !!this.jwtRepository.getJWToken();
  }

  getUser(): AuthUserDto | null {
    return this.jwtRepository.getUser();
  }

  getOtpMailData(): OtpMailData | null {
    return this.session.get<OtpMailData>(StorageKeyType.OtpSend);
  }

  saveTempRegisterData(data: RegisterFormData): void {
    this.session.set<RegisterFormData>(StorageKeyType.Register, data);
  }

  getRegisterData(): RegisterFormData | null {
    return this.session.get<RegisterFormData>(StorageKeyType.Register);
  }

  removeOnboardingData(): void {
    this.session.remove(StorageKeyType.Register);
    this.session.remove(StorageKeyType.OtpSend);
  }

  setConnecting(type: LoginMethodType): void {
    this.session.set<LoginMethodType>(StorageKeyType.AuthConnecting, type);
  }

  getConnecting(): LoginMethodType | null {
    return this.session.get<LoginMethodType>(StorageKeyType.AuthConnecting);
  }

  removeConnecting(): void {
    this.session.remove(StorageKeyType.AuthConnecting);
  }
}
